import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import { v4 as uuidv4 } from 'uuid';
// import { searchPhotos } from '../../utils/pexelsClient';
import UpgradeButton from '../../components/UpgradeButton';
import Modal from '../../components/Modal';
import FeedbackForm from '../../components/FeedbackForm';

function Create() {
  const navigate = useNavigate();

  const defaultWordCounts = { blog: 1200, page: 800 };
  const contentTypeLabels = { blog: 'Blog Post', page: 'Web Page' };

  const [contentType, setContentType] = useState('blog');
  const [wordCount, setWordCount] = useState(defaultWordCounts.blog);
  const [topic, setTopic] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [tone, setTone] = useState('friendly');
  const [audience, setAudience] = useState('general public');
  const [cta, setCta] = useState('none');
  const [customCta, setCustomCta] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [countdown, setCountdown] = useState(30);
  // const [showImage, setShowImage] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState('none');
  const [profiles, setProfiles] = useState([]);
  const [aiSummary, setAiSummary] = useState(false);
  const [planName, setPlanName] = useState('Free');
  const [generationsUsed, setGenerationsUsed] = useState(0);
  const [wordsUsed, setWordsUsed] = useState(0);
  const [searchParams] = useSearchParams();
  const [includeText, setIncludeText] = useState('');
  const includeMetaTags = 'yes';

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [hasFeedbackSubmitted, setHasFeedbackSubmitted] = useState(false);
  const feedbackRef = useRef(null);

  const isProfileSelected = selectedProfile !== 'none';

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      fetch("/api/confirm-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            console.log("✅ User upgraded to Pro.");
            setPlanName(data.plan); // 👈 immediately reflect updated plan
            window.history.replaceState({}, document.title, "/create"); // clean URL
          } else {
            console.error("⚠️ Failed to confirm session", data);
          }
        })
        .catch((err) => {
          console.error("Error confirming session:", err);
        });
    }
  }, [searchParams]);

  // This fetchProfiles supports a callback for after state is updated
  const fetchProfiles = async (newProfileId = null, cb = null) => {
    const { data: userData } = await supabase.auth.getUser();
    const user = userData?.user;
    if (!user) return;

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', user.id);

    if (error) {
      console.error('Error fetching profiles:', error);
    } else {
      const sortedProfiles = (data || []).sort((a, b) => a.name.localeCompare(b.name));
      setProfiles(sortedProfiles);

      if (newProfileId && sortedProfiles.some(p => p.id === newProfileId)) {
        setSelectedProfile(newProfileId);
        if (cb) cb(sortedProfiles.find(p => p.id === newProfileId));
      } else if (!sortedProfiles.length) {
        setSelectedProfile('none');
        if (cb) cb(null);
      } else if (cb) {
        cb(null);
      }
    }
  };

  useEffect(() => {
    const fetchPlanAndUsage = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: account } = await supabase
        .from('accounts')
        .select('plan')
        .eq('id', user.id)
        .single();

      const userPlan = account?.plan === 'pro' ? 'Pro' : 'Free';
      setPlanName(userPlan);

      if (userPlan === 'Free') {
        const { count } = await supabase
          .from('generated_content')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id);

        setGenerationsUsed(count || 0);
      } else {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
        const { data } = await supabase
          .from('generated_content')
          .select('word_count')
          .eq('user_id', user.id)
          .gte('created_at', startOfMonth);

        const total = (data || []).reduce((sum, row) => sum + (row.word_count || 0), 0);
        setWordsUsed(total);
      }

      setInitialLoading(false);
    };

    fetchPlanAndUsage();
    fetchProfiles();
  }, []);

  useEffect(() => {
    const handleProfileCreated = (e) => {
      const newProfileId = e.detail?.id;
      fetchProfiles(newProfileId, (profile) => {
        // Callback: sets voice/audience fields immediately after profile is fetched & selected
        if (profile) {
          setTone(profile.voice || 'friendly');
          setAudience(profile.audience || 'general');
        }
      });
    };

    window.addEventListener('profile-created', handleProfileCreated);
    return () => window.removeEventListener('profile-created', handleProfileCreated);
  }, []);

  useEffect(() => {
    if (!topic.trim()) {
      setSuggestions([]);
      return;
    }

    let cancelled = false;

    const delayDebounce = setTimeout(async () => {
      setLoadingSuggestions(true);
      try {
        const res = await fetch('/api/keyword-suggestions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ topic }),
        });

        const data = await res.json();
        if (!cancelled) {
          setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
        }
      } catch (err) {
        console.error('Error fetching keyword suggestions:', err);
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setLoadingSuggestions(false);
      }
    }, 800);

    return () => {
      clearTimeout(delayDebounce);
      cancelled = true;
    };
  }, [topic]);


  const handleProfileChange = (e) => {
    const value = e.target.value;
    setSelectedProfile(value);

    if (value === 'new-profile') {
      window.dispatchEvent(new Event('open-profile-modal'));
      setSelectedProfile('none');
      return;
    }

    const selected = profiles.find((p) => p.id === value);
    if (selected) {
      setTone(selected.voice || 'friendly');
      setAudience(selected.audience || 'general');
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();

    if (planName === 'Free' && generationsUsed >= 3) return;

    if (planName === 'Pro' && wordsUsed + wordCount > 60000) {
      alert("🚫 You have reached your 60,000 words monthly limit.");
      return;
    }

    setLoading(true);
    setCountdown(60);

    const newId = uuidv4();

    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    let profileData = null;
    if (selectedProfile !== 'none') {
      const selected = profiles.find((p) => p.id === selectedProfile);
      if (selected) {
        profileData = {
          name: selected.name || '',
          author: selected.author || '',
          domain: selected.domain || '',
          logo: selected.logo || ''
        };
      }
    }

    const body = {
      contentType,
      wordCount,
      topic,
      tone,
      audience,
      cta: cta === 'other' ? customCta : cta,
      includeMetaTags: includeMetaTags === 'yes',
      aiSummary,
      profile: profileData,
      includeText: includeText.trim() || null
    };

    try {
      const res = await fetch('/api/generate-content', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      const wordCountGenerated = data.content ? data.content.trim().split(/\s+/).length : 0;
      if (planName === 'Pro') {
        setWordsUsed(prev => prev + wordCountGenerated);
      } else {
        setGenerationsUsed(prev => prev + 1);
      }

      // let fetchedImageData = null;
      // if (showImage && topic) {
      //   const photos = await searchPhotos(topic, 1);
      //   if (photos.length > 0) fetchedImageData = photos[0];
      // }

      const { data: userData } = await supabase.auth.getUser();
      const user = userData?.user;

      // const altText = fetchedImageData?.alt || data.title || (suggestions.length > 0 ? suggestions[0].phrase : null);

      if (user) {
        const payload = {
          id: newId,
          user_id: user.id,
          content_type: contentType,
          title: data.title,
          slug: data.slug,
          excerpt: data.excerpt,
          content: data.content,
          head: includeMetaTags === 'yes' ? data.headRaw : null,
          ai_summary: data.aiSummaryHtml,
          // featured_image: fetchedImageData?.src?.medium || null,
          // photographer: fetchedImageData?.photographer || null,
          // photographer_url: fetchedImageData?.photographer_url || null,
          // image_alt: altText,
          profile: selectedProfile !== 'none' ? selectedProfile : null,
          word_count: wordCountGenerated,
          created_at: new Date().toISOString(),
          keywords: data.keyphrases?.split(',').map(k => k.trim()) || [],
        };

        console.log('🟡 Insert payload:', payload);

        const { error } = await supabase.from('generated_content').insert([payload]);

        if (error) {
          console.error('🔥 Supabase insert error:', error);
        }

        navigate(`/content/${newId}`);
      }

    } catch (err) {
      console.error(err);
      alert('Error generating content');
    } finally {
      clearInterval(countdownInterval);
      setCountdown(0);
      setLoading(false);
    }
  };

  const getResetDate = () => {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return nextMonth.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (initialLoading) {
    return (
      <div className="centered-loading">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <>
      <header>
        <div className="section-title">
          <h1>Create Content</h1>
        </div>
        <div className="breadcrumbs">
          <p>
            {planName === 'Free'
              ? <>Generations Used: <span>{generationsUsed}/3</span></>
              : <>Words Used: <span>{wordsUsed}/60000</span></>}
          </p>
        </div>
      </header>

      {/* Free limit */}
      {planName === 'Free' && generationsUsed >= 3 ? (
        <section className='welcome-page contain-smaller align-middle m-auto'>
          <h1>Unlock Full Access</h1>
          <p>
            You’ve reached your free limit of 3 generations.
          </p>
          <p>
            Upgrade for unlimited generations, up to 60,000 words a month plus priority support for <strong>$20/month</strong>.
          </p>
          <UpgradeButton>Upgrade to Pro</UpgradeButton>

          {/* Feedback prompt button */}
          <div style={{ margin: '32px 0 0 0', borderTop: '1px solid #eee', paddingTop: 20 }}>
            <button
              className="btn-blue mb-8"
              onClick={() => setShowFeedbackModal(true)}
            >
              Complete our quick feedback & get $5 off
            </button>
            <p>
              <b>Bonus:</b> Complete this survey and get a discount code to receive $5 off your first month when you subscribe!
            </p>
          </div>

          <Modal
            isOpen={showFeedbackModal}
            onClose={() => {
              setShowFeedbackModal(false);
              setTimeout(() => setHasFeedbackSubmitted(false), 300); // Reset for next time
            }}
            title="Your Experience"
            actions={[
              hasFeedbackSubmitted
                ? {
                    render: () => (
                      <UpgradeButton
                        className="btn-blue btn-success"
                      >
                        Upgrade to Pro with the Discount Code
                      </UpgradeButton>
                    )
                  }
                : {
                    label: 'Submit & Get $5 Off!',
                    className: 'btn-blue',
                    onClick: async () => {
                      if (feedbackRef.current) {
                        const didSubmit = await feedbackRef.current.submitForm();
                        if (didSubmit) setHasFeedbackSubmitted(true);
                      }
                    }
                  }
            ]}
          >
            <FeedbackForm ref={feedbackRef} onSubmitted={() => setHasFeedbackSubmitted(true)} />
          </Modal>
        </section>

        // Pro word limit
        ) : planName === 'Pro' && wordsUsed > 60000 ? (
        <section className='welcome-page contain-smaller align-middle m-auto'>
          <h1>Word Limit Reached</h1>
          <p>
            Your fresh batch of words resets on <strong>{getResetDate()}</strong>
          </p>
          <p>
            If you would like to use more words every month, please contact us for a custom plan.
          </p>
          <a href="mailto:carina@mglagency.com" className="btn">
            Request Custom Plan
          </a>
        </section>

        // Otherwise show the form
        ) : (
        <section className="scroll">
          <div className="contain">
            <form onSubmit={handleGenerate}>

            <div className="row">
              <div>
                <label htmlFor="profile">Select Profile:</label>
                <select id="profile" value={selectedProfile} onChange={handleProfileChange}>
                  <option value="none">None</option>
                  {profiles.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                  <option value="new-profile">+ Create Profile</option>
                </select>
              </div>
              <div>
                <label htmlFor="contentType">Content Type:</label>
                <select
                  id="contentType"
                  value={contentType}
                  onChange={(e) => {
                    const newContentType = e.target.value;
                    setContentType(newContentType);
                    setWordCount(defaultWordCounts[newContentType]);
                  }}
                >
                  {Object.keys(contentTypeLabels).map((key) => (
                    <option key={key} value={key}>
                      {contentTypeLabels[key]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="contained">
              <label htmlFor="topic">Describe Topic:</label>
              <input type="text" id="topic" value={topic} onChange={(e) => setTopic(e.target.value)} required />

              <div className="topic-suggestion-box">
                <table className="topic-table mt-0">
                  <thead>
                    <tr>
                      <th>Keyword Phrases</th>
                      <th>Est. Search Volume</th>
                      <th>Competition</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingSuggestions ? (
                      <tr>
                        <td colSpan="4">Loading suggestions...</td>
                      </tr>
                    ) : suggestions.length > 0 ? (
                      suggestions.map((s, i) => (
                        <tr key={i}>
                          <td>{s.phrase}</td>
                          <td>{s.volume}</td>
                          <td>{s.competition}</td>
                          <td>
                            <button type="button" className="btn-unset" onClick={() => setTopic(s.phrase)}>
                              Select
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4">No suggestions yet.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="row">
              <div>
                <label htmlFor="wordCount">Word Count:</label>
                <input
                  type="number"
                  id="wordCount"
                  value={wordCount}
                  min="300"
                  max="2500"
                  onChange={(e) => {
                    const value = Math.min(+e.target.value, 2500);
                    setWordCount(value);
                  }}
                />
              </div>
              {/* <div>
                <label htmlFor="image">Featured Image:</label>
                <select
                  id="image"
                  value={showImage ? 'yes' : 'no'}
                  onChange={(e) => setShowImage(e.target.value === 'yes')}
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div> */}
            </div>

            <div className="row">
              <div>
                <label htmlFor="tone">
                  Brand Voice:
                  {isProfileSelected && (
                    <span style={{ fontSize: 12, marginLeft: 8, color: "#aaa" }}>
                      (Set by profile)
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  id="tone"
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  placeholder="e.g. friendly, professional, witty"
                  disabled={isProfileSelected}
                  style={isProfileSelected ? { background: "#f5f5f5" } : undefined}
                />
              </div>

              <div>
                <label htmlFor="audience">
                  Audience:
                  {isProfileSelected && (
                    <span style={{ fontSize: 12, marginLeft: 8, color: "#aaa" }}>
                      (Set by profile)
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  id="audience"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  placeholder="e.g. General Public, Developers, Marketers"
                  disabled={isProfileSelected}
                  style={isProfileSelected ? { background: "#f5f5f5" } : undefined}
                />
              </div>
            </div>

            <div className="row g-0">
              <label htmlFor="includeText">Content to Include:</label>
              <textarea
                id="includeText"
                value={includeText}
                rows={5}
                onChange={(e) => setIncludeText(e.target.value)}
                placeholder="Important details, instructions, or must-have info to include in the content goes here."
              />
            </div>

            <div className="row">
              <div>
                <label htmlFor="cta">Call to Action:</label>
                <select
                  id="cta"
                  value={cta}
                  onChange={(e) => {
                    setCta(e.target.value);
                    if (e.target.value !== 'other') setCustomCta('');
                  }}
                >
                  <option value="sign-up">Sign Up Now</option>
                  <option value="get-started">Get Started</option>
                  <option value="try-free">Try It Free</option>
                  <option value="none">None</option>
                  <option value="other">Other (custom)</option>
                </select>
                {cta === 'other' && (
                  <input type="text" value={customCta} onChange={(e) => setCustomCta(e.target.value)} required />
                )}
              </div>
              <div>
                <label htmlFor="aiSummary">Focus on AI Search Ranking:</label>
                <select
                  id="aiSummary"
                  value={aiSummary ? 'yes' : 'no'}
                  onChange={(e) => setAiSummary(e.target.value === 'yes')}
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>

            <div className="content-actions">
              <button type="submit" className="btn cici-think" disabled={loading}>
                {loading ? `Generating… (${countdown}s)` : 'Generate Content'}
              </button>
            </div>
          </form>
        </div>
      </section>
      )}
    </>
  );
}

export default Create;